import React from 'react';
import { Stack, CircularProgress, Typography, Chip } from '@mui/material';
import { useSummaryFetch, WidgetLoadError } from './useSummaryFetch';
import { PERMISSIONS } from '../../../constants/permissions';
import { PayrollService } from '../../../services/payroll.service';
import { DashboardService } from '../../../services/dashboard.service';
import { PayrollSummary, PayrollPeriodTotal, DepartmentAmount, MonthlyHours, UnpaidWages, OtHoursWarning } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import StatTile from '../charts/StatTile';
import Columns from '../charts/Columns';
import { compactMoney, formatPeriodLabel } from '../charts/axisPadding';
import HBars from '../charts/HBars';
import { fmtMoney, CURRENCY_SYMBOL } from './format';

const DASHBOARD_PERMISSION = PERMISSIONS.DASHBOARD_VIEW;

const loadPayroll = (institutionId: string): Promise<PayrollSummary> =>
    DashboardService.getPayrollSummary(institutionId);

const PayrollWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadPayroll, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.lastRunPeriod == null) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No payroll run yet — generate payroll to see figures here.</Typography>;
    }
    return (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <StatTile label="Last run · net pay" unit={CURRENCY_SYMBOL} value={fmtMoney(d.lastRunTotal)} sub={formatPeriodLabel(d.lastRunPeriod)} />
            <StatTile label="Employees paid" value={d.headcount} />
        </Stack>
    );
};

const loadPayrollTrend = (institutionId: string): Promise<PayrollPeriodTotal[]> =>
    DashboardService.getPayrollTrend(institutionId);

const PayrollTrendWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadPayrollTrend, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No payroll runs yet — generate payroll to see totals by run.</Typography>;
    }
    return <Columns data={d.map(p => ({ label: p.period, value: p.netTotal }))} yFmt={fmtMoney} tickFmt={compactMoney} />;
};

const loadByDepartment = (institutionId: string): Promise<DepartmentAmount[]> =>
    DashboardService.getPayrollByDepartment(institutionId);

const PayrollByDepartmentWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadByDepartment, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No payroll run yet — generate payroll to see department totals.</Typography>;
    }
    return (
        <HBars
            data={d.map(g => ({
                label: g.department,
                value: g.amount,
                tooltip: [
                    { label: 'Net', value: fmtMoney(g.amount) },
                    { label: 'Headcount', value: String(g.headcount) },
                ],
            }))}
            vFmt={fmtMoney}
        />
    );
};

const loadOvertime = (institutionId: string): Promise<MonthlyHours[]> =>
    DashboardService.getPayrollOvertime(institutionId);

const PayrollOvertimeWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadOvertime, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No overtime recorded in this window.</Typography>;
    }
    // Hours, not money — no yFmt.
    return <Columns data={d.map(m => ({ label: m.month, value: m.hours }))} />;
};

const loadUnpaidWages = (institutionId: string): Promise<UnpaidWages> =>
    PayrollService.getUnpaidWages(institutionId);

// S4-04 (DA-01/02/12). The amount is the same paid-of-payable figure as /payroll/run; the deadline is the
// legal one (BD Labour Act s.123: 7th working day after the wage month ends), not the company payday.
const PayrollUnpaidWagesWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadUnpaidWages, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    const next = d.nextDeadline ? `Legal deadline ${d.nextDeadline}` : '';
    const sub = d.runs === 0
        ? 'All finalized runs paid'
        : `${formatPeriodLabel(d.items[0].period)} · ${next}${d.overdueRuns > 0 ? ' · past deadline' : ''}`;
    return (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <StatTile label="Unpaid wages" unit={CURRENCY_SYMBOL} value={fmtMoney(d.remaining)} sub={sub} tone={d.overdueRuns > 0 ? 'critical' : 'default'} to="/payroll/run" />
            <StatTile label="Runs with unpaid salary" value={d.runs} sub={d.overdueRuns > 0 ? `${d.overdueRuns} past the legal deadline` : undefined} />
        </Stack>
    );
};

const loadOtHoursWarning = (institutionId: string): Promise<OtHoursWarning> =>
    PayrollService.getOtHoursWarning(institutionId);

// S4-13: week-to-date punch hours over the legal week (BLA s.102). Off until the institution turns it on.
const PayrollOtHoursWarningWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadOtHoursWarning, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (!d.enabled) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>The 48/60-hour week warning is off. Turn it on in Payroll settings.</Typography>;
    }
    const since = `since Mon ${d.weekStart}`;
    if (d.rows.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>Everyone is within 48 hours this week ({since}).</Typography>;
    }
    return (
        <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">{d.over60Count} over 60h · {d.over48Count} over 48h · {since}</Typography>
            {d.rows.slice(0, 8).map(r => (
                <Stack key={r.userId} direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>{r.name}{r.employeeCode ? <Typography component="span" variant="caption" color="text.secondary"> · {r.employeeCode}</Typography> : null}</Typography>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
                        <Typography variant="body2">{r.hours}h</Typography>
                        <Chip size="small" color={r.level === 'OVER_60H' ? 'error' : 'warning'} label={r.level === 'OVER_60H' ? 'Over 60h' : 'Over 48h'} />
                    </Stack>
                </Stack>
            ))}
            {d.rows.length > 8 && <Typography variant="caption" color="text.secondary">and {d.rows.length - 8} more</Typography>}
        </Stack>
    );
};

export const payrollWidgets: WidgetDef[] = [
    { id: 'payroll.summary', module: 'payroll', title: 'Payroll — last run', subtitle: 'Net pay and headcount on the latest payroll run', permission: DASHBOARD_PERMISSION, size: 'md', component: PayrollWidget },
    { id: 'payroll.trend', module: 'payroll', title: 'Payroll — net total by run', subtitle: 'Net pay for each payroll run, last 6 runs', permission: DASHBOARD_PERMISSION, size: 'lg', component: PayrollTrendWidget },
    { id: 'payroll.byDepartment', module: 'payroll', title: 'Payroll — last run by department', subtitle: 'How the latest run\'s net pay splits across departments', permission: DASHBOARD_PERMISSION, size: 'md', component: PayrollByDepartmentWidget },
    { id: 'payroll.unpaidWages', module: 'payroll', title: 'Payroll — unpaid wages', subtitle: 'Finalized salary not yet paid, with the legal deadline (7th working day after the wage month)', permission: PERMISSIONS.PAYROLL_VIEW, size: 'lg', component: PayrollUnpaidWagesWidget },
    { id: 'payroll.overtime', module: 'payroll', title: 'Payroll — overtime hours', subtitle: 'Overtime hours recorded per month, last 6 months', permission: DASHBOARD_PERMISSION, size: 'md', component: PayrollOvertimeWidget },
    { id: 'payroll.otHoursWarning', module: 'payroll', title: 'Payroll — 48/60-hour week', subtitle: 'Employees over 48 hours (normal week) or 60 hours (with overtime) this week so far', permission: PERMISSIONS.PAYROLL_VIEW, size: 'lg', component: PayrollOtHoursWarningWidget },
];
